import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

export function StoriesBar({ stories = [], currentUserId }) {
  const scrollRef = useRef(null);
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [trendingStories, setTrendingStories] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    const fetchTrendingStories = async (retries = 2) => {
      if (stories.length > 0) return;
      if (!isAuthenticated) return;
      setLoadingTrending(true);
      try {
        const headers = getAuthHeaders();
        const res = await fetch(`${API}/stories/trending?limit=20`, { headers });
        if (res.ok) {
          const result = await safeJson(res);
          if (result.ok && result.data) setTrendingStories(result.data.stories || []);
        } else if (retries > 0) {
          await new Promise(r => setTimeout(r, 1000));
          return fetchTrendingStories(retries - 1);
        }
      } catch (err) {
        if (retries > 0) {
          await new Promise(r => setTimeout(r, 1000));
          return fetchTrendingStories(retries - 1);
        }
        console.warn('Trending stories fetch failed:', err?.message || err);
      } finally {
        setLoadingTrending(false);
      }
    };
    fetchTrendingStories();
  }, [stories.length, isAuthenticated, getAuthHeaders]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -240 : 240,
        behavior: 'smooth'
      });
    }
  };

  const displayStories = stories.length > 0 ? stories : trendingStories;

  // Group stories by user
  const groupedStories = displayStories.reduce((acc, story) => {
    const userId = story.user_id;
    if (!acc[userId]) {
      acc[userId] = {
        user_id: userId,
        handle: story.handle || story.user_handle,
        name: story.name || story.user_name,
        avatar: story.avatar || story.user_avatar,
        thumbnail: story.media_url || story.thumbnail || story.avatar || story.user_avatar,
        stories: [],
        has_unseen: false,
        story_count: 0,
      };
    }
    acc[userId].stories.push(story);
    acc[userId].story_count++;
    if (!story.viewed) acc[userId].has_unseen = true;
    // Use latest story's media as thumbnail
    if (story.media_url) acc[userId].thumbnail = story.media_url;
    return acc;
  }, {});

  const storyUsers = Object.values(groupedStories);
  const canPostStory = user?.role && ['artist', 'podcaster', 'streamer', 'videographer', 'designer', 'studio', 'owner', 'admin', 'producer', 'engineer'].includes(user.role);

  if (storyUsers.length === 0 && !loadingTrending) {
    return (
      <div className="relative mb-4 px-4" data-testid="stories-bar">
        <div className="flex gap-3 overflow-x-auto scrollbar-hide py-3 px-1">
          {canPostStory ? (
            <Link to="/stories/create" className="flex-shrink-0 group" data-testid="create-spotlight-btn">
              <div className="w-[72px] h-[72px] rounded-xl border border-dashed border-border bg-muted/10 flex flex-col items-center justify-center gap-1 group-hover:border-orange-500/40 group-hover:bg-orange-500/5 transition-all">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                  <Plus className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
                </div>
                <span className="text-[9px] font-semibold text-muted-foreground group-hover:text-orange-400 transition-colors">
                  Story
                </span>
              </div>
            </Link>
          ) : (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 mr-2 text-muted-foreground/80" />
              No spotlights yet
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-4" data-testid="stories-bar">
      {/* Scroll Left */}
      {showLeftArrow && storyUsers.length > 4 && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-background/70 border border-border flex items-center justify-center hover:bg-background/90 transition-all"
        >
          <ChevronLeft className="w-4 h-4 text-white" />
        </button>
      )}

      {/* Scroll Right */}
      {showRightArrow && storyUsers.length > 4 && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-background/70 border border-border flex items-center justify-center hover:bg-background/90 transition-all"
        >
          <ChevronRight className="w-4 h-4 text-white" />
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto scrollbar-hide py-3 px-4 snap-x"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Create Button */}
        {canPostStory && (
          <Link to="/stories/create" className="flex-shrink-0 group snap-start" data-testid="create-spotlight-btn">
            <div className="relative w-[72px] h-[72px] rounded-xl overflow-hidden border border-border bg-gradient-to-b from-zinc-800/50 to-zinc-900/80">
              {user?.picture ? (
                <img src={user.picture} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-900/30 to-zinc-900" />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/30">
                <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform">
                  <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <span className="text-[9px] font-semibold text-foreground/80">Story</span>
              </div>
            </div>
          </Link>
        )}

        {/* Loading */}
        {loadingTrending && (
          <div className="flex items-center justify-center px-8">
            <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
          </div>
        )}

        {/* Story Cards — Vertical Rectangles */}
        {storyUsers.map((storyUser, index) => (
          <Link
            key={storyUser.user_id}
            to={`/stories/${storyUser.handle}`}
            className="flex-shrink-0 group snap-start"
            style={{ animationDelay: `${index * 40}ms` }}
            data-testid={`story-card-${storyUser.handle}`}
          >
            <div className={`relative w-[72px] h-[72px] rounded-xl overflow-hidden ${
              storyUser.has_unseen
                ? 'ring-2 ring-orange-500 ring-offset-1 ring-offset-zinc-950'
                : 'border border-border'
            } group-hover:scale-[1.03] transition-transform duration-200`}>
              {/* Thumbnail background */}
              {storyUser.thumbnail ? (
                <img
                  src={storyUser.thumbnail}
                  alt={storyUser.name || storyUser.handle}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 via-zinc-800 to-zinc-900" />
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Unseen indicator dot */}
              {storyUser.has_unseen && (
                <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-orange-500 border border-black shadow-lg animate-pulse" />
              )}

              {/* Story count badge */}
              {storyUser.story_count > 1 && (
                <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-background/50 backdrop-blur-sm">
                  <span className="text-[9px] font-bold text-white">{storyUser.story_count}</span>
                </div>
              )}

              {/* Avatar + name at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className={`w-6 h-6 rounded-full flex-shrink-0 overflow-hidden border ${
                    storyUser.has_unseen ? 'border-orange-500' : 'border-border'
                  }`}>
                    {storyUser.avatar ? (
                      <img src={storyUser.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-[9px] font-bold text-white">
                          {(storyUser.name || storyUser.handle || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-[10px] font-semibold text-foreground leading-tight truncate">
                  {storyUser.handle ? `@${storyUser.handle}` : ""}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default StoriesBar;
